/**
 * TopCard.tsx — Tabbed card combining "About You" and "Simulation Parameters".
 *
 * Replaces the two stacked cards (ProfileCard + SimDetailsCard).
 * Tabs: About You | Simulation | Social Security
 */

import { useState } from 'react'
import type { SimulationParams, UserProfile } from '../engine/types'
import { RETIREMENT_401K_LIMIT } from '../engine/defaults'
import { annualSSBenefit } from '../engine/social_security'
import { useFinanceCopy } from '../CopyContext'

interface Props {
  params: SimulationParams
  onChange: (updated: SimulationParams) => void
  onRun: () => void
  running: boolean
  rememberMe: boolean
  onRememberMeChange: (v: boolean) => void
  onExport: () => void
  onImport: (file: File) => void
  onShare: () => void
  shareToast?: string | null
  loadedFromShare?: boolean
}

function isProfileComplete(profile: UserProfile): boolean {
  return (
    profile.birthYear >= 1940 &&
    profile.birthYear <= 2010 &&
    (profile.annualW2Income > 0 || profile.annualSelfEmploymentIncome > 0) &&
    profile.annualHousingExpense > 0 &&
    profile.annualOtherExpenses > 0
  )
}

function fmt(v: number) {
  if (v >= 1_000_000) return `$${(v / 1_000_000).toFixed(1)}M`
  if (v >= 1_000) return `$${Math.round(v / 1_000)}k`
  return `$${v}`
}

function pct(v: number) { return `${Math.round(v * 100)}%` }

type Tab = 'profile' | 'simulation' | 'ss'

export function TopCard({ params, onChange, onRun, running, rememberMe, onRememberMeChange, onExport, onImport, onShare, shareToast, loadedFromShare }: Props) {
  const [tab, setTab] = useState<Tab>('profile')
  const copy = useFinanceCopy()

  function set<K extends keyof SimulationParams>(key: K, value: SimulationParams[K]) {
    onChange({ ...params, [key]: value })
  }
  function setProfile<K extends keyof UserProfile>(key: K, value: UserProfile[K]) {
    onChange({ ...params, profile: { ...params.profile, [key]: value } })
  }

  const { profile } = params
  const complete = isProfileComplete(profile)
  const totalIncome = profile.annualW2Income + profile.annualEquityComp + profile.annualSelfEmploymentIncome
  const totalExpenses = profile.annualHousingExpense + profile.annualOtherExpenses
  const suggestedTarget = totalExpenses > 0 ? Math.round(totalExpenses * 25 / 100_000) * 100_000 : 0
  const cash = Math.max(0, 1 - params.allocation.equity - params.allocation.bonds)

  return (
    <div className={`top-card ${complete ? 'top-card--complete' : ''}`}>
      {/* ── Tab bar ── */}
      <div className="top-card-tabs">
        <button
          className={`top-tab ${tab === 'profile' ? 'top-tab--active' : ''}`}
          onClick={() => setTab('profile')}
        >
          {copy.topTabAboutYou}
          {!complete && <span className="top-tab-badge">!</span>}
        </button>
        <button
          className={`top-tab ${tab === 'simulation' ? 'top-tab--active' : ''}`}
          onClick={() => setTab('simulation')}
        >
          {copy.topTabSimulation}
        </button>
        <button
          className={`top-tab ${tab === 'ss' ? 'top-tab--active' : ''}`}
          onClick={() => setTab('ss')}
        >
          {copy.topTabSS}
        </button>

        <div className="top-card-actions">
          {complete && tab !== 'profile' && (
            <span className="top-card-summary">
              {fmt(totalIncome)}/yr · {fmt(totalExpenses)} expenses
            </span>
          )}
          <span className="top-card-privacy">{copy.privacyBadge}</span>

          <button className="top-action-btn" onClick={onExport} title={copy.exportTitle}>
            {copy.btnExport}
          </button>
          <label className="top-action-btn top-action-btn--import" title={copy.importTitle}>
            {copy.btnImport}
            <input type="file" accept=".json" style={{ display: 'none' }}
              onChange={e => { const f = e.target.files?.[0]; if (f) { onImport(f); e.target.value = '' } }} />
          </label>
          <button className="top-action-btn top-action-btn--share" onClick={onShare} title={copy.shareTitle}>
            {shareToast ?? copy.btnShare}
          </button>

          <button
            className="run-btn-compact"
            onClick={onRun}
            disabled={!complete || running}
            title={!complete ? copy.btnRunIncompleteTitle : copy.btnRunTitle}
          >
            {running ? copy.btnRunning : copy.btnRun}
          </button>
        </div>
      </div>

      {/* ── About You tab ── */}
      {tab === 'profile' && (
        <>
          {!loadedFromShare && (
            <div className="profile-callout">
              <span className="profile-callout-icon">📊</span>
              <span>{copy.calloutDefaults}</span>
              <label className="profile-remember-label">
                <input type="checkbox" checked={rememberMe} onChange={e => onRememberMeChange(e.target.checked)} />
                <span>{copy.calloutRemember}</span>
              </label>
              <span className="profile-callout-privacy">{copy.calloutPrivacy}</span>
            </div>
          )}
          {loadedFromShare && (
            <div className="profile-callout profile-callout--share">
              <span className="profile-callout-icon">🔗</span>
              <span>{copy.calloutShare}</span>
              <label className="profile-remember-label">
                <input type="checkbox" checked={rememberMe} onChange={e => onRememberMeChange(e.target.checked)} />
                <span>{copy.calloutRemember}</span>
              </label>
              <span className="profile-callout-privacy">{copy.calloutPrivacy}</span>
            </div>
          )}
          <div className="top-card-grid">
          <div className="top-col">
            <div className="top-col-label">Identity</div>
            <TopField label="Your birth year">
              <input type="number" min={1940} max={2010} placeholder="e.g. 1985"
                value={profile.birthYear || ''}
                onChange={e => setProfile('birthYear', Number(e.target.value))} />
            </TopField>
            <TopField label="Partner birth year" hint="optional">
              <input type="number" min={1940} max={2010} placeholder="skip if single"
                value={profile.partnerBirthYear || ''}
                onChange={e => setProfile('partnerBirthYear', Number(e.target.value))} />
            </TopField>
            <TopField label="Children" hint="count">
              <input type="number" min={0} max={10} placeholder="0"
                value={profile.childrenCount}
                onChange={e => setProfile('childrenCount', Number(e.target.value))} />
            </TopField>
          </div>

          <div className="top-col">
            <div className="top-col-label">Annual Income</div>
            <TopField label="Salary + bonus (W2)">
              <input type="number" step={5000} min={0} placeholder="e.g. 100,000"
                value={profile.annualW2Income || ''}
                onChange={e => setProfile('annualW2Income', Number(e.target.value))} />
            </TopField>
            <TopField label="Equity / RSUs" hint="optional">
              <input type="number" step={5000} min={0} placeholder="0"
                value={profile.annualEquityComp || ''}
                onChange={e => setProfile('annualEquityComp', Number(e.target.value))} />
            </TopField>
            {profile.annualEquityComp > 0 && (
              <TopField label="Equity over N years" hint="0=indefinite">
                <input type="number" step={1} min={0} max={10} placeholder="e.g. 4"
                  value={profile.equityCompYears || ''}
                  onChange={e => setProfile('equityCompYears', Number(e.target.value))} />
              </TopField>
            )}
            <TopField label="Self-employment" hint="optional">
              <input type="number" step={5000} min={0} placeholder="0"
                value={profile.annualSelfEmploymentIncome || ''}
                onChange={e => setProfile('annualSelfEmploymentIncome', Number(e.target.value))} />
            </TopField>
            {totalIncome > 0 && <p className="top-col-total">Total: {fmt(totalIncome)}/yr</p>}
          </div>

          <div className="top-col">
            <div className="top-col-label">Starting Assets</div>
            <TopField label="Liquid savings">
              <input type="number" step={5000} min={0} placeholder="e.g. 50,000"
                value={profile.liquidAssets || ''}
                onChange={e => setProfile('liquidAssets', Number(e.target.value))} />
            </TopField>
            <TopField label="Retirement accounts">
              <input type="number" step={5000} min={0} placeholder="e.g. 150,000"
                value={profile.retirementAssets || ''}
                onChange={e => setProfile('retirementAssets', Number(e.target.value))} />
            </TopField>
            <TopField label="Home equity" hint="0 if renting">
              <input type="number" step={5000} min={0} placeholder="0"
                value={profile.homeEquity || ''}
                onChange={e => setProfile('homeEquity', Number(e.target.value))} />
            </TopField>
          </div>

          <div className="top-col">
            <div className="top-col-label">Annual Expenses</div>
            <TopField label="Housing (mortgage/rent)">
              <input type="number" step={1000} min={0} placeholder="e.g. 24,000"
                value={profile.annualHousingExpense || ''}
                onChange={e => setProfile('annualHousingExpense', Number(e.target.value))} />
            </TopField>
            <TopField label="All other spending">
              <input type="number" step={1000} min={0} placeholder="e.g. 40,000"
                value={profile.annualOtherExpenses || ''}
                onChange={e => setProfile('annualOtherExpenses', Number(e.target.value))} />
            </TopField>
            {totalExpenses > 0 && (
              <p className="top-col-total">
                Total: {fmt(totalExpenses)}/yr
                {suggestedTarget > 0 && (
                  <> · 25× = <strong>{fmt(suggestedTarget)}</strong></>
                )}
              </p>
            )}
            {totalIncome > 0 && totalExpenses > 0 && (() => {
              const net = totalIncome - totalExpenses
              return (
                <p className={`top-col-net ${net >= 0 ? 'top-col-net--pos' : 'top-col-net--neg'}`}>
                  {net >= 0 ? '▲' : '▼'} Net {net >= 0 ? '+' : ''}{fmt(net)}/yr after expenses
                </p>
              )
            })()}
          </div>
        </div>
        </>
      )}

      {/* ── Simulation tab ── */}
      {tab === 'simulation' && (
        <div className="top-card-grid">
          <div className="top-col">
            <div className="top-col-label">Asset Allocation</div>
            <SliderRow label="Equity" value={params.allocation.equity} display={pct(params.allocation.equity)}
              onChange={v => set('allocation', { ...params.allocation, equity: v, bonds: Math.min(params.allocation.bonds, 1 - v - params.allocation.cash) })} />
            <SliderRow label="Bonds" value={params.allocation.bonds} display={pct(params.allocation.bonds)}
              onChange={v => set('allocation', { ...params.allocation, bonds: v, cash: Math.max(0, 1 - params.allocation.equity - v) })} />
            <div className="top-static-row"><span>Cash</span><span>{pct(cash)}</span></div>
          </div>

          <div className="top-col">
            <div className="top-col-label">Return Assumptions</div>
            <NumberRow label="Equity mean" value={params.returns.equityMean} step={0.005} min={-0.1} max={0.5}
              onChange={v => set('returns', { ...params.returns, equityMean: v })} />
            <NumberRow label="Equity std dev" value={params.returns.equityStd} step={0.005} min={0} max={0.5}
              onChange={v => set('returns', { ...params.returns, equityStd: v })} />
            <NumberRow label="Inflation" value={params.inflation.mean} step={0.005} min={0} max={0.15}
              onChange={v => set('inflation', { ...params.inflation, mean: v })} />
          </div>

          <div className="top-col">
            <div className="top-col-label">Retirement</div>
            <TopField label="Retire year">
              <input type="number" min={2030} max={2070} value={params.retirementYear}
                onChange={e => set('retirementYear', Number(e.target.value))} />
            </TopField>
            <TopField label="Life expectancy">
              <input type="number" min={75} max={110} value={params.lifeExpectancy}
                onChange={e => set('lifeExpectancy', Number(e.target.value))} />
            </TopField>
            <TopField label="Post-retire spend/yr">
              <input type="number" step={5000} min={0} value={params.postRetirementExpenses}
                onChange={e => set('postRetirementExpenses', Number(e.target.value))} />
            </TopField>
          </div>

          <div className="top-col">
            <div className="top-col-label">Planning</div>
            <TopField label="401k contribution">
              <input type="number" step={1000} min={0} max={RETIREMENT_401K_LIMIT} value={params.annualRetirementContribution}
                onChange={e => set('annualRetirementContribution', Number(e.target.value))} />
            </TopField>
            <TopField label="Retirement target">
              <input type="number" step={100000} min={0} value={params.retirementTargetNetWorth}
                onChange={e => set('retirementTargetNetWorth', Number(e.target.value))} />
            </TopField>
            {suggestedTarget > 0 && params.retirementTargetNetWorth !== suggestedTarget && (
              <p className="top-col-total">
                25× expenses:{' '}
                <button className="btn-link" onClick={() => set('retirementTargetNetWorth', suggestedTarget)}>
                  {fmt(suggestedTarget)}
                </button>
              </p>
            )}
            <TopField label="Iterations">
              <select value={params.iterations} onChange={e => set('iterations', Number(e.target.value))}>
                <option value={500}>500 (fast)</option>
                <option value={1000}>1,000</option>
                <option value={3000}>3,000</option>
                <option value={5000}>5,000</option>
                <option value={10000}>10,000</option>
              </select>
            </TopField>
          </div>
        </div>
      )}

      {/* ── Social Security tab ── */}
      {tab === 'ss' && (
        <div className="top-card-grid">
          <SSPersonColumns
            label="You"
            config={params.userSS}
            onChange={cfg => set('userSS', cfg)}
          />
          <SSPersonColumns
            label="Partner"
            config={params.spouseSS}
            onChange={cfg => set('spouseSS', cfg)}
          />
        </div>
      )}
    </div>
  )
}

// ─── SS person columns ────────────────────────────────────────────────────────

function SSPersonColumns({
  label, config, onChange,
}: {
  label: string
  config: SimulationParams['userSS']
  onChange: (cfg: SimulationParams['userSS']) => void
}) {
  const benefit = annualSSBenefit(config.maxContribYears, config.nearMaxContribYears, config.nearMaxFraction, config.claimingAge)
  const benefitFmt = `${fmt(Math.round(benefit / 12))}/mo (${fmt(Math.round(benefit))} /yr)`

  return (
    <>
      <div className="top-col">
        <div className="top-col-label">{label}</div>
        <TopField label="Birth year">
          <input type="number" min={1960} max={2000} value={config.birthYear || ''}
            onChange={e => onChange({ ...config, birthYear: Number(e.target.value) })} />
        </TopField>
        <TopField label="Claiming age">
          <input type="number" min={62} max={70} value={config.claimingAge}
            onChange={e => onChange({ ...config, claimingAge: Number(e.target.value) })} />
        </TopField>
        <TopField label="Max contrib years">
          <input type="number" min={0} max={50} value={config.maxContribYears}
            onChange={e => onChange({ ...config, maxContribYears: Number(e.target.value) })} />
        </TopField>
        <TopField label="Near-max years">
          <input type="number" min={0} max={35} value={config.nearMaxContribYears}
            onChange={e => onChange({ ...config, nearMaxContribYears: Number(e.target.value) })} />
        </TopField>
        <p className="top-col-total">Est. benefit at {config.claimingAge}: <strong>{benefitFmt}</strong></p>
      </div>
    </>
  )
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function TopField({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <label className="top-field">
      <span className="top-field-label">
        {label}{hint && <em> ({hint})</em>}
      </span>
      {children}
    </label>
  )
}

function SliderRow({ label, value, display, onChange }: {
  label: string; value: number; display: string; onChange: (v: number) => void
}) {
  return (
    <div className="top-slider-row">
      <span>{label}</span>
      <input type="range" min={0} max={1} step={0.05} value={value}
        onChange={e => onChange(Number(e.target.value))} />
      <span className="top-val">{display}</span>
    </div>
  )
}

function NumberRow({ label, value, step, min, max, onChange }: {
  label: string; value: number; step: number; min: number; max: number; onChange: (v: number) => void
}) {
  return (
    <div className="top-number-row">
      <span>{label}</span>
      <input type="number" step={step} min={min} max={max} value={value}
        onChange={e => onChange(Number(e.target.value))} />
      <span className="top-val">{pct(value)}</span>
    </div>
  )
}
